
import React, { useLayoutEffect } from 'react';
import ResultsContent from './results/ResultsContent';
import { useResultsData } from '@/hooks/useResultsData';
import { calculateTripleObjectivityScores } from '@/utils/assessmentScoring';
import { AssessmentData } from '@/types/resultsTypes';
import { CategoryScores } from '@/types/assessmentTypes';
import { sigmaToPercentile } from '@/utils/personalityCalculations';
import { ASSESSMENT_WORDS } from '@/data/assessmentWords';
import { calculateBehavioralScores } from '@/utils/scoreCalculations';
import { isLowConfidenceResult } from '@/utils/assessmentValidation';

interface ResultsDashboardProps {
  data: AssessmentData;
  onBackToHome: () => void;
}

const EMPTY_TRAITS = {
  dominance: 0,
  extraversion: 0,
  patience: 0,
  formality: 0,
  objectivity: 0,
  objectivityModel3: 0,
};

function logDataSanity(label: string, d: any) {
  // Only log in development/custom build
  if (typeof window !== 'undefined' && window?.location?.hostname?.includes('lovable')) {
    // Always log for diagnosis (disable if too noisy)
    // eslint-disable-next-line no-console
    console.log(`[ResultsDashboard LOG] ${label}:`, JSON.parse(JSON.stringify(d)));
  }
}

// Enhanced function to extract trait scores using the updated scoreCalculations
// For M3 assessments, use stored percentiles directly instead of z-scores
function extractTraitScores(
  obj: any,
  selectedWordIds?: number[],
  isM3Assessment?: boolean
): {
  dominance: number;
  extraversion: number;
  patience: number;
  formality: number;
  objectivity: number;
  objectivityModel3: number;
  // M3 percentiles (direct values)
  dominancePercentile?: number;
  extraversionPercentile?: number;
  patiencePercentile?: number;
  formalityPercentile?: number;
  objectivityPercentile?: number;
} {
  logDataSanity("extractTraitScores input", { obj, isM3Assessment });
  
  try {
    // For M3 assessments, check if percentiles are stored directly
    const scoresData = obj?.scores || obj;
    
    if (isM3Assessment && scoresData?.dominancePercentile !== undefined) {
      // M3: Use stored percentiles directly, z-scores are also stored
      const result = {
        dominance: scoresData.dominance ?? 0,
        extraversion: scoresData.extraversion ?? 0,
        patience: scoresData.patience ?? 0,
        formality: scoresData.formality ?? 0,
        objectivity: scoresData.objectivity ?? 0,
        objectivityModel3: scoresData.objectivity ?? 0,
        // Include direct percentiles for M3
        dominancePercentile: scoresData.dominancePercentile,
        extraversionPercentile: scoresData.extraversionPercentile,
        patiencePercentile: scoresData.patiencePercentile,
        formalityPercentile: scoresData.formalityPercentile,
        objectivityPercentile: scoresData.objectivityPercentile,
      };
      logDataSanity("extractTraitScores M3 result", result);
      return result;
    }
    
    // M1/M2: Use the enhanced calculateBehavioralScores function
    const calculatedScores = calculateBehavioralScores(obj);
    
    const result = {
      dominance: calculatedScores.aScore,
      extraversion: calculatedScores.bScore,
      patience: calculatedScores.cScore,
      formality: calculatedScores.dScore,
      objectivity: calculatedScores.objectivityModel2 || calculatedScores.eScore || 0,
      objectivityModel3: calculatedScores.objectivityModel3 || calculatedScores.eScore || 0,
    };

    logDataSanity("extractTraitScores result", result);
    return result;
  } catch (error) {
    console.error('Error calculating behavioral scores:', error);
    throw error; // Re-throw to be handled by component
  }
}

const ResultsDashboard: React.FC<ResultsDashboardProps> = ({ data, onBackToHome }) => {
  // Log incoming raw data for diagnosis
  logDataSanity('ResultsDashboard data prop', data);

  const isDualAssessment = (data as any).isDualAssessment === true;
  
  // Detect M3 assessment from assessment_variant
  const isM3Assessment = (data as any).assessmentVariant === 'model3' || 
                        (data as any).assessment_variant === 'model3';

  // Check for low-confidence results based on duration
  const durationSeconds = (data as any).durationSeconds ?? null;
  const lowConfidence = isLowConfidenceResult(durationSeconds, isDualAssessment);

  // All local holders
  let scores = EMPTY_TRAITS;
  let selectedWordsText: string[] = [];
  let selectedWords: number[] = [];

  let selfWordsText: string[] = [];
  let workWordsText: string[] = [];
  let selfSelectedWords: number[] = [];
  let workSelectedWords: number[] = [];
  let selfTraitScores: any = EMPTY_TRAITS;
  let workTraitScores: any = EMPTY_TRAITS;
  
  let selfSelectedWordsText: string[] = [];
  let workSelectedWordsText: string[] = [];

  let criticalDataIssue = null; // Fallback for error reporting

  // === DUAL FLOW ===
  if (isDualAssessment) {
    const dualData = data as any;
    // Early fallback check
    if (!dualData?.selfBehavior || !dualData?.workBehavior) {
      criticalDataIssue = 'Missing selfBehavior or workBehavior data in dual assessment';
      logDataSanity('ResultsDashboard dual assessment error: missing behavior', dualData);
    } else {
      try {
        // Self
        selfWordsText = Array.isArray(dualData?.selfBehavior?.selectedWordsText) ? dualData.selfBehavior.selectedWordsText : [];
        selfSelectedWords = Array.isArray(dualData?.selfBehavior?.selectedWords) ? dualData.selfBehavior.selectedWords : [];
        selfTraitScores = extractTraitScores(dualData.selfBehavior, selfSelectedWords, isM3Assessment);
        selfSelectedWordsText = selfWordsText;

        // Work
        workWordsText = Array.isArray(dualData?.workBehavior?.selectedWordsText) ? dualData.workBehavior.selectedWordsText : [];
        workSelectedWords = Array.isArray(dualData?.workBehavior?.selectedWords) ? dualData.workBehavior.selectedWords : [];
        workTraitScores = extractTraitScores(dualData.workBehavior, workSelectedWords, isM3Assessment);
        workSelectedWordsText = workWordsText;
      } catch (error) {
        criticalDataIssue = `Assessment data error: ${error instanceof Error ? error.message : 'Unable to calculate personality scores'}`;
        logDataSanity('ResultsDashboard dual assessment scoring error', error);
      }

      // Pick the main set for summary stuff (not charts)
      // Note: validity checks moved below to avoid duplication
      // Validity will be checked below after hooks
      const tempSelfValid = Object.values(selfTraitScores).some((v) => typeof v === "number" && !isNaN(v));
      const tempWorkValid = Object.values(workTraitScores).some((v) => typeof v === "number" && !isNaN(v));
      
      if (tempSelfValid) {
        scores = selfTraitScores;
        selectedWordsText = selfWordsText;
        selectedWords = selfSelectedWords;
      } else if (tempWorkValid) {
        scores = workTraitScores;
        selectedWordsText = workWordsText;
        selectedWords = workSelectedWords;
      } else {
        criticalDataIssue = "Could not extract valid trait scores from either self or work behavior in dual assessment.";
        logDataSanity('ResultsDashboard dual: Both profiles invalid', { selfTraitScores, workTraitScores, dualData });
        scores = EMPTY_TRAITS;
      }
    }
  }
  // === SINGLE FLOW ===
  else {
    if (data && typeof data === 'object') {
      try {
        // Accept flexible: If .scores exists, use, else try data object itself.
        let toExtract = (data as any)?.scores ?? data;
        selectedWordsText = Array.isArray((data as any)?.selectedWordsText) ? (data as any).selectedWordsText : [];
        selectedWords = Array.isArray((data as any)?.selectedWords) ? (data as any).selectedWords : [];
        if (selectedWords.length === 0 && Array.isArray((data as any)?.selected_words)) {
          // Fix for alternative keys
          selectedWords = (data as any).selected_words;
        }
        scores = extractTraitScores(toExtract, selectedWords, isM3Assessment);
      } catch (error) {
        criticalDataIssue = `Assessment data error: ${error instanceof Error ? error.message : 'Unable to calculate personality scores'}`;
        logDataSanity('ResultsDashboard single assessment scoring error', error);
      }

      // FIXED: Accept any valid number including negative values
      const validAny = Object.values(scores).some((v) => typeof v === "number" && !isNaN(v));
      if (!validAny) {
        criticalDataIssue = "No valid behavioral scores found in this single assessment data.";
        logDataSanity('ResultsDashboard single: invalid scores', { scores, data });
      }
    } else {
      criticalDataIssue = "No assessment data found.";
      logDataSanity('ResultsDashboard: data type error', data);
      scores = EMPTY_TRAITS;
    }
  }

  // Prepare content and percentages for "objectivity"
  const categoryScores: CategoryScores = {
    dominance: scores.dominance || 0,
    extraversion: scores.extraversion || 0,
    patience: scores.patience || 0,
    formality: scores.formality || 0,
    objectivity: scores.objectivity || 0,
  };
  const tripleScores = calculateTripleObjectivityScores(
    categoryScores,
    ASSESSMENT_WORDS,
    new Set(selectedWords)
  );
  const objectivityScore = tripleScores.objectivityModel3;
  const objectivityPercentile = sigmaToPercentile(objectivityScore);

  // Helper to check if scores are valid AND not just default zeros
  const hasRealScores = (traitScores: any) => {
    return Object.values(traitScores).some((v: any) => 
      typeof v === "number" && !isNaN(v) && v !== 0
    );
  };

  // Check validity to determine which profile(s) to use
  const selfValid = isDualAssessment && hasRealScores(selfTraitScores);
  const workValid = isDualAssessment && hasRealScores(workTraitScores);

  // Extract stored profile names from DB data (source of truth, same as dashboard)
  const storedProfileName: string | null = isDualAssessment
    ? null  // Dual: profile name is derived from self behavior; passed per-branch below
    : ((data as any)?.profileName || (data as any)?.profile_name || null);

  const selfStoredProfileName: string | null = isDualAssessment
    ? ((data as any)?.selfBehavior?.profileName || (data as any)?.selfProfileName || null)
    : null;

  const workStoredProfileName: string | null = isDualAssessment
    ? ((data as any)?.workBehavior?.profileName || (data as any)?.workProfileName || null)
    : null;

  // Call hooks for all three modes — pass stored profile name so results match dashboard
  const selfBehavioralData = useResultsData(selfTraitScores, selfStoredProfileName);
  const workBehavioralData = useResultsData(workTraitScores, workStoredProfileName);
  const singleBehavioralData = useResultsData(scores, storedProfileName);

  // Use the appropriate profile based on assessment type
  const mainBehavioralData = isDualAssessment
    ? (selfValid ? selfBehavioralData : workBehavioralData)
    : singleBehavioralData;
  const mainBehavioralFactors = mainBehavioralData.behavioralFactors;
  const objectivityFactor = mainBehavioralData?.objectivityFactor || { name: "Style", score: 0, percentile: 0, key: "objectivity", description: "", color: "", insights: "" };
  const personalityProfile = mainBehavioralData.personalityProfile || null;

  // Enhanced logging for dual assessment data
  if (isDualAssessment) {
    console.log('ResultsDashboard - Dual assessment personality profiles:', {
      selfProfile: selfBehavioralData?.personalityProfile,
      workProfile: workBehavioralData?.personalityProfile,
      selfFactorsCount: selfBehavioralData?.behavioralFactors?.length || 0,
      workFactorsCount: workBehavioralData?.behavioralFactors?.length || 0
    });
  }

  // Ensure scroll to top on mount
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // If a critical error detected in flow, render a proper error page
  if (criticalDataIssue) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
        <div className="bg-white border border-red-200 rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Assessment Error</h2>
            <p className="text-gray-600 mb-6">
              We were unable to process your assessment results. This typically happens when the assessment data is incomplete or corrupted.
            </p>
          </div>
          
          <div className="space-y-3">
            <button
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              onClick={() => window.location.href = '/'}
            >
              Retake Assessment
            </button>
            <button
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              onClick={onBackToHome}
            >
              Back to Home
            </button>
          </div>
          
          <p className="text-xs text-gray-400 mt-4">
            If this problem persists, please contact support.
          </p>
        </div>
      </div>
    );
  }

  // Extract user data for cognitive assessment flow
  const userData = {
    fullName: (data as any).fullName || (data as any).full_name || '',
    email: (data as any).email || ''
  };

  // Extract company_id
  const companyId = (data as any).companyId || (data as any).company_id || null;

  // Extract candidate's assessment language
  const candidateLanguage = (data as any).candidateLanguage || 'en';

  // All normal content
  return (
    <div className="min-h-screen">
      <div id="dashboard-content" className="w-full">
        <ResultsContent
          behavioralFactors={mainBehavioralFactors}
          objectivityFactor={objectivityFactor}
          personalityProfile={personalityProfile}
          selectedWordsText={selectedWordsText}
          selectedWordsCount={selectedWordsText.length}
          objectivityScore={objectivityFactor.score}
          scores={scores}
          completedAt={data.completedAt}
          isDualAssessment={isDualAssessment}
          selfBehavioralFactors={isDualAssessment && selfValid ? selfBehavioralData?.behavioralFactors ?? [] : []}
          workBehavioralFactors={isDualAssessment && workValid ? workBehavioralData?.behavioralFactors ?? [] : []}
          selfWordsCount={isDualAssessment && selfValid ? (selfWordsText.length || 0) : 0}
          workWordsCount={isDualAssessment && workValid ? (workWordsText.length || 0) : 0}
          selfSelectedWordsText={isDualAssessment && selfValid ? selfSelectedWordsText : []}
          workSelectedWordsText={isDualAssessment && workValid ? workSelectedWordsText : []}
          selfPersonalityProfile={isDualAssessment && selfValid ? selfBehavioralData?.personalityProfile : undefined}
          workPersonalityProfile={isDualAssessment && workValid ? workBehavioralData?.personalityProfile : undefined}
          userData={userData}
          companyId={companyId}
          candidateLanguage={candidateLanguage}
          lowConfidence={lowConfidence}
        />
      </div>
    </div>
  );
};

export default ResultsDashboard;

